import {BBModel} from '../../bb_model';
import {PersonSet} from '../../collection/sets/person_set';
import {SlotSet} from '../../collection/sets/slot_set';
import {Mutex} from '../../utils/mutex';
import {PersonModel} from '../core/person';

class AdminPersonModel extends PersonModel {

    constructor(data) {
        super(data);
        this.title = this.name;

        // person_companies always includes the parent so check if there are more than 2
        // to determine if the person works in multiple locations.
        if (this.person_companies && this.person_companies.length > 2) {
            this.isMultiLocationPerson = true;
        }
    }

    /**
     * @description
     * Set current customer
     *
     * @returns {Promise} Returns a promise that rezolve the current customer
     */
    setCurrentCustomer() {
        return new Promise((resolve, reject) => {
            if (this.$has('queuer')) {
                this.$get('queuer').then((queuer) => {
                    this.serving = new BBModel.Admin.Queuer(queuer);
                    resolve(this.serving);
                }, err => reject(err));
            } else {
                resolve();
            }
        });
    }

    /**
     * @param {string} status The status of attendance
     * @param {string} duration The estimated duration
     * @description
     * Set attendance in according of the status parameter
     *
     * @returns {Promise} Returns a promise that rezolve the attendance
     */
    setAttendance(data) {
        return new Promise((resolve, reject) => {
            if (data.liveStatus) {
                data.liveStatus.setAttendance(this, data.status, data.duration, data.queueId)
                    .then((status) => {
                        resolve(this);
                    });
            } else {
                this.$put('attendance', {}, {status, estimated_duration: duration, client_queue_id: queueId}).then((p) => {
                    this.updateModel(p);
                    if (status === 2) {
                        this.updateEstimatedReturn(duration);
                    }
                    if (!this.$has('queuer')) this.serving = null;
                    resolve(this);
                }, (err) => {
                    reject(err);
                });
            }
        });
    }

    /**
     * @description
     * Finish serving
     *
     * @returns {Promise} Returns a promise that resolve the finish serving
     */
    finishServing(status, liveStatus) {
        return new Promise((resolve, reject) => {
            if (liveStatus) {
                liveStatus.finishServing(this, status).then((status) => {
                    resolve(this);
                });
            } else {
                this.attendance_status = 1; // immediately mark this as available
                this.serving = null;
                Mutex.getLock().then((mutex) => {
                    if (this.$has('finish_serving')) {
                        this.$flush('self');
                        this.$post('finish_serving').then((q) => {
                                this.$get('self').then((p) => {
                                    this.updateModel(p);
                                    if (!this.$has('queuer')) this.serving = null;
                                    resolve(q);
                                    Mutex.unlock(mutex);
                                }, (err) => {
                                    reject(err);
                                    Mutex.unlock(mutex);
                                });
                            }
                            , (err) => {
                                reject(err);
                                Mutex.unlock(mutex);
                            });
                    } else {
                        reject('finish_serving link not available');
                        Mutex.unlock(mutex);
                    }
                });
            }
        });
    }

    /**
     * @param {date=} start The start date format of the availability schedule
     * @param {date=} end The end date format of the availability schedule
     * @description
     * Look up a schedule for a time range to see if this available.
     *
     * @returns {string} Returns yes if schedule is available or not.
     */
    // look up a schedule for a time range to see if this available
    // currently just checks the date - but chould really check the time too
    isAvailable(start, end) {
        const str = start.format('YYYY-MM-DD') + '-' + end.format('YYYY-MM-DD');
        if (!this.availability) {
            this.availability = {};
        }

        if (this.availability[str]) {
            return this.availability[str] === 'Yes';
        }
        this.availability[str] = '-';

        if (this.$has('schedule')) {
            this.$get('schedule', {
                start_date: start.format('YYYY-MM-DD'),
                end_date: end.format('YYYY-MM-DD')
            }).then((sched) => {
                    this.availability[str] = 'No';
                    if (sched && sched.dates && sched.dates[start.format('YYYY-MM-DD')] && (sched.dates[start.format('YYYY-MM-DD')] !== 'None')) {
                        return this.availability[str] = 'Yes';
                    }
                }
            );
        } else {
            this.availability[str] = 'Yes';
        }

        return this.availability[str] === 'Yes';
    }

    /**
     * @param {string=} queuer The queuer of the company.
     * @description
     * Start serving in according of the queuer parameter
     *
     * @returns {Promise} Returns a promise that rezolve the start serving link
     */
    startServing(queueId, liveStatus, queuer) {
        return new Promise((resolve, reject) => {
            if (liveStatus) {
                liveStatus.startServing(this, queueId, queuer)
                    .then((status) => {
                        resolve(this);
                });
            } else {
                Mutex.getLock().then((mutex) => {
                    if (this.$has('start_serving')) {
                        this.$flush('self');
                        const params = {queuer_id: queuer ? queuer.id : null, person_id: this.id};
                        this.$post('start_serving', params).then((q) => {
                            this.$get('self').then((p) => {
                                this.updateModel(p);
                                this.serving = new BBModel.Admin.Queuer(q);
                                resolve(this.serving);
                                Mutex.unlock(mutex);
                            }, (err) => {
                                Mutex.unlock(mutex);
                                return reject(err);
                            });
                        }, (err) => {
                            Mutex.unlock(mutex);
                            return reject(err);
                        });
                    } else {
                        reject('start_serving link not available');
                        Mutex.unlock(mutex);
                    }
                });
            }
        });
    }

    /**
     * @description
     * Get the queuers
     *
     * @returns {Promise} Returns a promise that rezolve the queuer links
     */
    getQueuers() {
        return new Promise((resolve, reject) => {
            if (this.$has('queuers')) {
                this.$flush('queuers');
                this.$get('queuers').then((collection) => {
                    collection.$get('queuers').then((queuers) => {
                        const models = (Array.from(queuers).map(q => new BBModel.Admin.Queuer(q)));
                        this.queuers = models;
                        resolve(models);
                    }, (err) => {
                        reject(err);
                    });
                }, (err) => {
                    return reject(err);
                });
            } else {
                reject('queuers link not available');
            }
        });
    }

    updateEstimatedReturn(estimate) {
        const start = this.attendance_started;
        if (!estimate) estimate = this.attendance_estimate;
        if (start && estimate) {
            this.estimated_return = moment(start).add(estimate, 'minutes').format('LT');
        }
    }


    setLiveStatus(status) {
        this.liveStatus = status;
    }

    currentStatus() {
        if (this.liveStatus && this.liveStatus.Person(this)) {
            return this.liveStatus.Person(this);
        }
        return {};
    }

    /**
     * @description
     * Get post data
     *
     * @returns {array} Returns data
     */
    getPostData() {
        const data = {};
        data.id = this.id;
        data.name = this.name;
        data.extra = this.extra;
        data.description = this.description;
        return data;
    }

    /**
     * @param {object} data The company data
     * @description
     * Update the data in according of the data parameter
     *
     * @returns {array} Returns the updated array
     */
    $update(data) {
        if (!data) {
            data = this.getPostData();
        }
        const post_data = {...data}
        // do an basic stringify test of the arrays of Who/What/Where data and don't send if it didn't change
        if (JSON.stringify(post_data.service_ids) == JSON.stringify(this._data.service_ids))
            delete post_data.service_ids;
        if (JSON.stringify(post_data.resource_ids) == JSON.stringify(this._data.resource_ids))
            delete post_data.resource_ids;

        return this.$put('self', {}, this.jsonify(post_data)).then((res) => {
            PersonSet.updateAll(this.self, this.company_id);
            this.constructor(res);
        });
    }

    $delete() {
        this.$flush('self');
        return this.$del('self').then((res) => {
            PersonSet.delete(this.self, this.company_id);
        });
    }

    static $new(person) {
        person = new BBModel.Admin.Person(person);
        return person;
    }

    static $block(company, person, data) {
        return new Promise((resolve, reject) => {
            person.$put('block', {}, data).then((response) => {
                    SlotSet.updateAll(response.$link('slot').href);
                    if (response.$href('self').indexOf('bookings') > -1) {
                        const booking = new BBModel.Admin.Booking(response);
                        return resolve(booking);
                    } else {
                        const slot = new BBModel.Admin.Slot(response);
                        return resolve(slot);
                    }
                }
                , (err) => {
                    return reject(err);
                }
            );
        });
    }

    static $signup(user, data) {

        return user.$get('company').then((company) => {
            return new Promise((resolve, reject) => {
                const params = {};
                company.$post('people', params, data).then((person) => {
                        if (person.$has('administrator')) {
                            return person.$get('administrator').then((user) => {
                                login.setLogin(user);
                                return resolve(person);
                            });
                        } else {
                            return resolve(person);
                        }
                    }
                    , err => reject(err));
            });
        });
    }

    $refetch() {
        return new Promise((resolve, reject) => {
            this.$flush('self');
            this.$get('self').then((res) => {
                    this.constructor(res);
                    return resolve(this);
                }
                , err => reject(err));
        });
    }
}

export {
    AdminPersonModel
};